import { useState } from 'react';
import { Globe, RefreshCw, Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { useWebsiteCrawlForm } from '@/hooks/useWebsiteCrawlForm';
import type { IKBWebsitesTabProps, TKBProcessingStatus } from '@/@types/knowledgeBase';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: component in src/components/knowledgeBase/ needs convex Id type

const STATUS_LABEL: Record<TKBProcessingStatus, string> = {
  pending: 'Pending',
  processing: 'Crawling',
  completed: 'Ready',
  failed: 'Failed',
};

const STATUS_VARIANT: Record<
  TKBProcessingStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  // LOCAL_TYPE_JUSTIFIED: inline Record value type for Badge variant — not a standalone type definition
  pending: 'secondary',
  processing: 'default',
  completed: 'default',
  failed: 'destructive',
};

function StatusBadge({ status }: { status: TKBProcessingStatus }) {
  // LOCAL_TYPE_JUSTIFIED: inline prop destructure in a private helper — not an exported interface
  return <Badge variant={STATUS_VARIANT[status]}>{STATUS_LABEL[status]}</Badge>;
}

export function KnowledgeBaseWebsitesTab({
  documents,
  isLoading,
  onCrawl,
  onDelete,
  onToggleShared,
  onRefresh,
}: IKBWebsitesTabProps) {
  const {
    state,
    setUrl,
    setDepth,
    setIsShared,
    setIsCrawling,
    isValidUrl,
    toggleSelect,
    selectAll,
    resetForm,
  } = useWebsiteCrawlForm();

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const websiteDocs = documents.filter(d => d.sourceType === 'website');

  const handleCrawl = async () => {
    if (!isValidUrl(state.url)) return;
    setIsCrawling(true);
    try {
      await onCrawl({
        url: state.url,
        depth: parseInt(state.depth, 10),
        isSharedWithCompanies: state.isShared,
      });
      resetForm();
    } finally {
      setIsCrawling(false);
    }
  };

  const handleBulkDelete = async () => {
    setIsDeleting(true);
    try {
      const ids = Array.from(state.selectedIds) as Id<'knowledgeBaseDocuments'>[]; // AS_JUSTIFIED: Set<Id<'knowledgeBaseDocuments'>> values are already Id type, cast is safe
      await onDelete(ids);
      selectAll([]);
    } finally {
      setIsDeleting(false);
    }
  };

  const allIds = websiteDocs.map(d => d._id);
  const allSelected = allIds.length > 0 && allIds.every(id => state.selectedIds.has(id));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
        Loading websites...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Inline crawl form */}
      <div className="flex items-end gap-3 p-4 bg-muted/40 rounded-lg border border-border">
        <div className="flex-1 space-y-1">
          <Label htmlFor="website-url">Website URL</Label>
          <Input
            id="website-url"
            placeholder="https://example.com"
            value={state.url}
            onChange={e => setUrl(e.target.value)}
            disabled={state.isCrawling}
          />
        </div>
        <div className="w-32 space-y-1">
          <Label htmlFor="crawl-depth">Depth</Label>
          <Select
            value={state.depth}
            onValueChange={val => setDepth(val as '1' | '2' | '3' | '4' | '5')} // AS_JUSTIFIED: Select onValueChange returns string, but value is constrained to depth literals by SelectItem values
            disabled={state.isCrawling}
          >
            <SelectTrigger id="crawl-depth">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(['1', '2', '3', '4', '5'] as const).map(d => (
                <SelectItem key={d} value={d}>
                  {d}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 pb-1">
          <Switch
            id="crawl-shared"
            checked={state.isShared}
            onCheckedChange={setIsShared}
            disabled={state.isCrawling}
          />
          <Label htmlFor="crawl-shared" className="text-sm cursor-pointer">
            Shared
          </Label>
        </div>
        <Button
          onClick={() => void handleCrawl()}
          disabled={state.isCrawling || !isValidUrl(state.url)}
        >
          {state.isCrawling ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
          ) : (
            <Globe className="w-4 h-4 mr-2" aria-hidden="true" />
          )}
          {state.isCrawling ? 'Crawling...' : 'Crawl'}
        </Button>
      </div>

      {/* Bulk delete bar */}
      {state.selectedIds.size > 0 && (
        <div className="flex items-center justify-between px-4 py-2 bg-muted rounded-lg border border-border">
          <span className="text-sm text-muted-foreground">{state.selectedIds.size} selected</span>
          <Button variant="destructive" size="sm" onClick={() => setIsDeleteConfirmOpen(true)}>
            <Trash2 className="w-4 h-4 mr-2" aria-hidden="true" />
            Delete selected
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="bg-table-container border border-table-container-border rounded-2xl overflow-hidden shadow">
        <div className="grid grid-cols-[2rem_1fr_6rem_7rem_8rem_3rem] gap-3 px-4 py-3 border-b border-table-row-border text-xs font-medium text-muted-foreground uppercase tracking-wide">
          <Checkbox
            checked={allSelected}
            onCheckedChange={() => selectAll(allIds)}
            aria-label="Select all"
          />
          <span>Website</span>
          <span>Shared</span>
          <span>Status</span>
          <span>Added</span>
          <span />
        </div>

        {websiteDocs.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Globe className="w-10 h-10 text-muted-foreground mb-3" aria-hidden="true" />
            <p className="text-sm font-medium text-foreground mb-1">No websites crawled yet</p>
            <p className="text-xs text-muted-foreground">Enter a URL above to crawl a website.</p>
          </div>
        )}

        {websiteDocs.map(doc => (
          <div
            key={doc._id}
            className={cn(
              'grid grid-cols-[2rem_1fr_6rem_7rem_8rem_3rem] gap-3 items-center px-4 py-3 border-b border-table-row-border last:border-b-0',
              state.selectedIds.has(doc._id) && 'bg-muted/50'
            )}
          >
            <Checkbox
              checked={state.selectedIds.has(doc._id)}
              onCheckedChange={() => toggleSelect(doc._id)}
              aria-label={`Select ${doc.title}`}
            />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{doc.title}</p>
              {doc.websiteUrl && (
                <p className="text-xs text-muted-foreground truncate">{doc.websiteUrl}</p>
              )}
            </div>
            <Switch
              checked={doc.isSharedWithCompanies}
              onCheckedChange={checked => void onToggleShared(doc._id, checked)}
              aria-label="Toggle shared"
            />
            <StatusBadge status={doc.processingStatus} />
            <span className="text-xs text-muted-foreground">
              {new Date(doc.createdAt).toLocaleDateString()}
            </span>
            <Button
              variant="ghost"
              size="icon"
              disabled={doc.processingStatus === 'processing'}
              onClick={() => void onRefresh(doc._id)}
              aria-label="Refresh website"
            >
              <RefreshCw
                className={cn('w-4 h-4', doc.processingStatus === 'processing' && 'animate-spin')}
                aria-hidden="true"
              />
            </Button>
          </div>
        ))}
      </div>

      <AlertDialog
        open={isDeleteConfirmOpen}
        onOpenChange={open => {
          if (!open) setIsDeleteConfirmOpen(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-red-100 p-2 dark:bg-red-900/30">
                <AlertTriangle
                  aria-hidden="true"
                  className="h-5 w-5 text-red-600 dark:text-red-400"
                />
              </div>
              <AlertDialogTitle>
                Delete{' '}
                {state.selectedIds.size === 1 ? '1 website' : `${state.selectedIds.size} websites`}?
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="pt-2">
              This will permanently remove the selected websites from your knowledge base and the
              RAG search index. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={isDeleting}
              onClick={() => void handleBulkDelete().then(() => setIsDeleteConfirmOpen(false))}
            >
              {isDeleting ? (
                <>
                  <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                `Delete ${state.selectedIds.size === 1 ? '1 website' : `${state.selectedIds.size} websites`}`
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
